import { create } from 'zustand';
import { api } from '../services/api';

export interface FlightAttribute {
  id: string;
  project_id: string;
  name: string;
  slug: string;
  description: string | null;
  color: string;
  icon: string | null;
  is_system: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  flight_count?: number;
}

interface CreateAttributeInput {
  name: string;
  slug: string;
  description?: string;
  color?: string;
  icon?: string;
}

interface UpdateAttributeInput {
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  sort_order?: number;
}

interface FlightAttributesState {
  attributes: FlightAttribute[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchAttributes: (projectId: string) => Promise<void>;
  createAttribute: (projectId: string, data: CreateAttributeInput) => Promise<FlightAttribute>;
  updateAttribute: (attributeId: string, data: UpdateAttributeInput) => Promise<void>;
  deleteAttribute: (attributeId: string) => Promise<void>;
  reorderAttributes: (projectId: string, attributeIds: string[]) => Promise<void>;
  copyAttributes: (projectId: string, sourceProjectId: string, attributeIds: string[]) => Promise<FlightAttribute[]>;

  // Flight assignment actions
  getFlightAttributes: (flightId: string) => Promise<FlightAttribute[]>;
  setFlightAttributes: (flightId: string, attributeIds: string[]) => Promise<FlightAttribute[]>;

  clearError: () => void;
  clearAttributes: () => void;
}

export const useFlightAttributesStore = create<FlightAttributesState>((set, get) => ({
  attributes: [],
  isLoading: false,
  error: null,

  fetchAttributes: async (projectId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ data: FlightAttribute[] }>(`/projects/${projectId}/attributes`);
      set({ attributes: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Attribute';
      set({ error: message, isLoading: false });
    }
  },

  createAttribute: async (projectId, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: FlightAttribute }>(`/projects/${projectId}/attributes`, data);
      const newAttribute = response.data;
      set((state) => ({
        attributes: [...state.attributes, newAttribute].sort((a, b) => a.sort_order - b.sort_order),
        isLoading: false,
      }));
      return newAttribute;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Erstellen des Attributs';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateAttribute: async (attributeId, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.put<{ data: FlightAttribute }>(`/attributes/${attributeId}`, data);
      const updatedAttribute = response.data;
      set((state) => ({
        attributes: state.attributes
          .map((a) => (a.id === attributeId ? updatedAttribute : a))
          .sort((a, b) => a.sort_order - b.sort_order),
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Aktualisieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deleteAttribute: async (attributeId) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`/attributes/${attributeId}`);
      set((state) => ({
        attributes: state.attributes.filter((a) => a.id !== attributeId),
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Löschen';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  reorderAttributes: async (projectId, attributeIds) => {
    // Optimistic update
    set((state) => {
      const reordered = attributeIds
        .map((id, index) => {
          const attr = state.attributes.find((a) => a.id === id);
          return attr ? { ...attr, sort_order: index } : null;
        })
        .filter((a): a is FlightAttribute => a !== null);

      // Add system attributes (they stay at the top)
      const systemAttrs = state.attributes.filter((a) => a.is_system);
      const customAttrs = reordered.filter((a) => !a.is_system);

      return {
        attributes: [...systemAttrs, ...customAttrs].sort((a, b) => a.sort_order - b.sort_order),
      };
    });

    try {
      await api.put(`/projects/${projectId}/attributes/reorder`, { attribute_ids: attributeIds });
    } catch (error) {
      // Refetch on error
      const message = error instanceof Error ? error.message : 'Fehler beim Sortieren';
      set({ error: message });
      get().fetchAttributes(projectId);
      throw error;
    }
  },

  copyAttributes: async (projectId, sourceProjectId, attributeIds) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: FlightAttribute[] }>(`/projects/${projectId}/attributes/copy`, {
        source_project_id: sourceProjectId,
        attribute_ids: attributeIds,
      });
      const copiedAttributes = response.data;
      set((state) => ({
        attributes: [...state.attributes, ...copiedAttributes].sort((a, b) => a.sort_order - b.sort_order),
        isLoading: false,
      }));
      return copiedAttributes;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Kopieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  getFlightAttributes: async (flightId) => {
    try {
      const response = await api.get<{ data: FlightAttribute[] }>(`/flights/${flightId}/attributes`);
      return response.data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden';
      set({ error: message });
      throw error;
    }
  },

  setFlightAttributes: async (flightId, attributeIds) => {
    try {
      const response = await api.put<{ data: FlightAttribute[] }>(`/flights/${flightId}/attributes`, {
        attribute_ids: attributeIds,
      });
      return response.data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Setzen der Attribute';
      set({ error: message });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  clearAttributes: () => {
    set({ attributes: [], error: null });
  },
}));

// Helper: Generate slug from name
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[äöüß]/g, (match) => {
      const map: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };
      return map[match] || match;
    })
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// Preset colors for new attributes
export const ATTRIBUTE_COLORS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#ef4444', // Red
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#14b8a6', // Teal
  '#3b82f6', // Blue
  '#06b6d4', // Cyan
  '#84cc16', // Lime
  '#a855f7', // Purple
];

// Preset icons
export const ATTRIBUTE_ICONS = [
  { value: 'document-text', label: 'Dokument' },
  { value: 'currency-euro', label: 'Euro' },
  { value: 'megaphone', label: 'Megafon' },
  { value: 'sparkles', label: 'Sterne' },
  { value: 'fire', label: 'Feuer' },
  { value: 'heart', label: 'Herz' },
  { value: 'star', label: 'Stern' },
  { value: 'bolt', label: 'Blitz' },
  { value: 'calendar', label: 'Kalender' },
  { value: 'camera', label: 'Kamera' },
  { value: 'video-camera', label: 'Video' },
  { value: 'music-note', label: 'Musik' },
];
